import { useEffect, useState } from "react";
import {
  acceptCurrentLegalConsent,
  getMyContributionState,
  setMyContributionAuthorization,
} from "./consent-gate";

type SurveyConsentSettingsProps = {
  visible: boolean;
  identityKey: string;
};

function messageFromError(error: unknown) {
  if (error instanceof Error) return error.message;
  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof (error as { message?: unknown }).message === "string"
  ) {
    return String((error as { message: string }).message);
  }
  return "No se pudo actualizar la autorizacion.";
}

export function SurveyConsentSettings({
  visible,
  identityKey,
}: SurveyConsentSettingsProps) {
  const [authorized, setAuthorized] = useState(false);
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [legalCheck, setLegalCheck] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const loadState = async () => {
    const state = await getMyContributionState();
    setAuthorized(Boolean(state?.contributionAuthorized));
    setLegalAccepted(Boolean(state?.termsAccepted && state?.privacyAccepted));
  };

  useEffect(() => {
    let active = true;

    if (!visible || !identityKey) {
      setLoaded(false);
      setMessage(null);
      return () => {
        active = false;
      };
    }

    setLoaded(false);
    setMessage(null);

    void getMyContributionState()
      .then((state) => {
        if (!active) return;
        setAuthorized(Boolean(state?.contributionAuthorized));
        setLegalAccepted(Boolean(state?.termsAccepted && state?.privacyAccepted));
        setLoaded(true);
      })
      .catch((error: unknown) => {
        if (!active) return;
        setMessage(messageFromError(error));
        setLoaded(true);
      });

    return () => {
      active = false;
    };
  }, [visible, identityKey]);

  if (!visible) return null;

  const acceptLegal = async () => {
    if (busy || !loaded || legalAccepted || !legalCheck) return;

    setBusy(true);
    setMessage(null);

    try {
      await acceptCurrentLegalConsent();
      await loadState();
      setLegalCheck(false);
      setMessage("Terminos y privacidad aceptados.");
    } catch (error: unknown) {
      setMessage(messageFromError(error));
    } finally {
      setBusy(false);
    }
  };

  const updateAuthorization = async (next: boolean) => {
    if (busy || !loaded || !legalAccepted) return;

    const previous = authorized;
    setBusy(true);
    setMessage(null);
    setAuthorized(next);

    try {
      const saved = await setMyContributionAuthorization(next);
      setAuthorized(Boolean(saved));
      setMessage(next ? "Contribucion autorizada." : "Contribucion desactivada.");
    } catch (error: unknown) {
      setAuthorized(previous);
      setMessage(messageFromError(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="survey-consent-settings" aria-label="Contribucion de datos">
      <div>
        <strong>Contribucion de datos</strong>
        <p>
          Es opcional y revocable. THERA funciona normalmente si esta desactivada.
        </p>
      </div>

      {!legalAccepted && loaded && (
        <div className="survey-consent-legacy">
          <p>
            Antes de activar la contribucion, confirma los terminos y la politica
            de privacidad vigentes.
          </p>
          <label>
            <input
              type="checkbox"
              checked={legalCheck}
              disabled={busy}
              onChange={(event) => setLegalCheck(event.target.checked)}
            />
            <span>
              He leido y acepto los terminos de uso y la politica de privacidad.
            </span>
          </label>
          <button type="button" disabled={!legalCheck || busy} onClick={() => void acceptLegal()}>
            {busy ? "Guardando..." : "Aceptar y continuar"}
          </button>
        </div>
      )}

      <label>
        <input
          type="checkbox"
          checked={authorized}
          disabled={!loaded || busy || !legalAccepted}
          onChange={(event) => void updateAuthorization(event.target.checked)}
        />
        <span>{authorized ? "Autorizada" : "No autorizada"}</span>
      </label>

      {!loaded && <small>Cargando preferencia...</small>}
      {message && <small role="status">{message}</small>}
    </section>
  );
}